import { useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { ChevronDown, ChevronLeft, ChevronUp, PawPrint, Plus } from "lucide-react"
import { hasEntitlement, ENTITLEMENTS } from "~/utils/entitlements"
import {
  archiveDog,
  deleteDog,
  fetchDogs,
  reorderDogs,
  restoreDog,
  updateDog,
  type DogProfile,
} from "~/api/dogs"
import { EmptyState } from "~/components/EmptyState"
import { SearchableBreedSelector } from "~/components/SearchableBreedSelector"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"

export function ManageDogsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [dogs, setDogs] = useState<DogProfile[]>([])
  const [archivedDogs, setArchivedDogs] = useState<DogProfile[]>([])
  const [hasPlus, setHasPlus] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState("")
  const [editBreed, setEditBreed] = useState("")
  const [editMixDetail, setEditMixDetail] = useState("")
  const [breedValid, setBreedValid] = useState(true)
  const [saving, setSaving] = useState(false)
  const [openedFromQuery, setOpenedFromQuery] = useState(false)

  async function load() {
    const data = await fetchDogs()
    setDogs(data.dogs)
    setArchivedDogs(data.archived_dogs ?? [])
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([fetchDogs(), import("~/api/auth").then((m) => m.getProfile())])
      .then(([dogsData, profile]) => {
        setDogs(dogsData.dogs)
        setArchivedDogs(dogsData.archived_dogs ?? [])
        setHasPlus(hasEntitlement(profile.user.entitlements, ENTITLEMENTS.multipleDogs))
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Could not load dogs"))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (openedFromQuery || dogs.length === 0) return
    const raw = searchParams.get("dog_id")
    if (!raw) return
    const dogId = Number(raw)
    if (!Number.isFinite(dogId) || dogId <= 0) return
    const match = dogs.find((dog) => dog.id === dogId && !dog.plus_locked)
    if (!match) return
    setEditingId(match.id)
    setEditName(match.name)
    setEditBreed(match.breed ?? "")
    setEditMixDetail(match.mix_detail ?? "")
    setBreedValid(true)
    setOpenedFromQuery(true)
  }, [dogs, searchParams, openedFromQuery])

  async function handleArchive(dogId: number) {
    try {
      await archiveDog(dogId)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive")
    }
  }

  async function handleRestore(dogId: number) {
    try {
      await restoreDog(dogId)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not restore")
    }
  }

  async function handleDelete(dogId: number, name: string) {
    const confirmed = window.confirm(
      `Permanently delete ${name}?\n\nThis removes ${name}'s profile, Story, portraits, photos, notes/observations, saved Ask Dynamo conversations, and all associated memories. This cannot be undone.`,
    )
    if (!confirmed) return
    try {
      await deleteDog(dogId)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete")
    }
  }

  async function moveDog(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= dogs.length) return
    const next = [...dogs]
    const [item] = next.splice(index, 1)
    next.splice(target, 0, item)
    setDogs(next)
    try {
      const data = await reorderDogs(next.map((dog) => dog.id))
      setDogs(data.dogs)
      setArchivedDogs(data.archived_dogs ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reorder")
      await load()
    }
  }

  async function saveEdit(dogId: number) {
    if (!editName.trim() || saving || !breedValid) return
    setSaving(true)
    try {
      await updateDog(dogId, {
        name: editName.trim(),
        breed: editBreed || null,
        mix_detail: editBreed ? editMixDetail || null : null,
      })
      setEditingId(null)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update dog")
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
      <header className="mb-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronLeft size={20} aria-hidden />
          </button>
          <h1 className="flex-1 text-lg font-semibold text-[var(--color-text-primary)]">
            Manage Dogs
          </h1>
          {hasPlus && (
            <button
              type="button"
              onClick={() => navigate("/pack/add")}
              className="flex items-center gap-1 rounded-lg bg-[var(--color-brand-primary)] px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              <Plus size={16} aria-hidden />
              Add Dog
            </button>
          )}
      </header>

      <div>
        {error && (
          <p className="mb-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {loading ? (
          <div
            className="space-y-3"
            role="status"
            aria-label="Loading dogs"
            data-testid="manage-dogs-loading"
          >
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                className="animate-pulse rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4"
              >
                <div className="h-4 w-1/3 rounded bg-[var(--color-divider)]" />
                <div className="mt-3 h-3 w-2/3 rounded bg-[var(--color-divider)]" />
              </div>
            ))}
          </div>
        ) : dogs.length === 0 ? (
          <EmptyState
            icon={<PawPrint size={48} className="text-[var(--color-text-secondary)]" />}
            title="No dogs in your pack yet"
            description="Complete an assessment to add your first dog, then grow your pack with Plus."
            action={
              <Link
                to="/assessment"
                className="rounded-lg bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-medium text-white"
              >
                Start assessment
              </Link>
            }
          />
        ) : (
          <div className="space-y-3">
            {dogs.map((dog, index) => (
              <div
                key={dog.id}
                className="rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4"
              >
                <div className="flex items-start gap-3">
                  {hasPlus && dogs.length > 1 && (
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        aria-label={`Move ${dog.name} up`}
                        disabled={index === 0}
                        onClick={() => void moveDog(index, -1)}
                        className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] disabled:opacity-30"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${dog.name} down`}
                        disabled={index === dogs.length - 1}
                        onClick={() => void moveDog(index, 1)}
                        className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] disabled:opacity-30"
                      >
                        <ChevronDown size={16} />
                      </button>
                    </div>
                  )}
                  <div className="flex-1">
                    {editingId === dog.id ? (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <input
                            value={editName}
                            onChange={(event) => setEditName(event.target.value)}
                            className="flex-1 rounded-lg border border-[var(--color-divider)] px-2 py-1 text-sm"
                            aria-label={`Edit ${dog.name}`}
                          />
                          <button
                            type="button"
                            disabled={saving || !breedValid}
                            onClick={() => void saveEdit(dog.id)}
                            className="text-sm font-medium text-[var(--color-brand-primary)] disabled:opacity-40"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="text-sm text-[var(--color-text-secondary)]"
                          >
                            Cancel
                          </button>
                        </div>
                        <SearchableBreedSelector
                          value={editBreed}
                          mixDetail={editMixDetail}
                          onChange={(breed, mixDetail) => {
                            setEditBreed(breed)
                            setEditMixDetail(mixDetail ?? "")
                          }}
                          onValidityChange={setBreedValid}
                          placeholder="Search or select breed (optional)"
                        />
                      </div>
                    ) : (
                      <>
                        <p className="font-medium text-[var(--color-text-primary)]">{dog.name}</p>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                          {dog.archetype_label}
                        </p>
                        {dog.plus_locked && (
                          <p className="mt-1 text-xs font-medium text-amber-800">
                            Plus-locked — safe and restored with Plus
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-3 text-xs">
                  {!dog.plus_locked && (
                  <button
                    type="button"
                    className="font-medium text-[var(--color-brand-primary)] underline-offset-2 hover:underline"
                    onClick={() => {
                      setEditingId(dog.id)
                      setEditName(dog.name)
                      setEditBreed(dog.breed ?? "")
                      setEditMixDetail(dog.mix_detail ?? "")
                      setBreedValid(true)
                    }}
                  >
                    Edit
                  </button>
                  )}
                  {hasPlus && dogs.length > 1 && (
                    <button
                      type="button"
                      className="text-[var(--color-text-secondary)] underline-offset-2 hover:underline"
                      onClick={() => void handleArchive(dog.id)}
                    >
                      Archive
                    </button>
                  )}
                  {!dog.plus_locked && (
                  <button
                    type="button"
                    className="text-red-600 underline-offset-2 hover:underline"
                    onClick={() => void handleDelete(dog.id, dog.name)}
                  >
                    Delete
                  </button>
                  )}
                  {dog.plus_locked && (
                    <Link to="/plus" className="font-medium text-[var(--color-brand-primary)] underline">
                      Restore with Plus
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <section className="mt-8">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
            Archived
          </h2>
          {archivedDogs.length === 0 ? (
            <EmptyState
              title="No archived dogs"
              description="Dogs you archive stay out of the selector, but their Story and Ask history remain safe."
            />
          ) : (
            <div className="space-y-3">
              {archivedDogs.map((dog) => (
                <div
                  key={dog.id}
                  className="rounded-xl border border-dashed border-[var(--color-divider)] bg-[var(--color-surface-card)]/70 p-4"
                >
                  <p className="font-medium text-[var(--color-text-primary)]">{dog.name}</p>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs">
                    <button
                      type="button"
                      className="font-medium text-[var(--color-brand-primary)] underline-offset-2 hover:underline"
                      onClick={() => navigate(`/story?dog_id=${dog.id}`)}
                    >
                      View Story
                    </button>
                    <button
                      type="button"
                      className="text-[var(--color-brand-primary)] underline-offset-2 hover:underline"
                      onClick={() => void handleRestore(dog.id)}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className="text-red-600 underline-offset-2 hover:underline"
                      onClick={() => void handleDelete(dog.id, dog.name)}
                    >
                      Delete permanently
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
